import { EventType, MatrixEvent, RoomEvent as MatrixRoomEvent } from 'matrix-js-sdk/src/matrix';
import { useEffect } from 'react';

import notificationBeep from 'assets/sounds/notificationBeep.mp3';
import useLiveKitDevicesStateContext from 'contexts/LiveKitDevicesStateContext/useLiveKitDevicesStateContext';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useSoundProvider from 'contexts/SoundProvider/useSoundProvider';
import useDefinedMatrixValues from 'hooks/useDefinedMatrixValues';
import useLatest from 'hooks/useLatest';
import { emojis } from 'modules/MeetingReactions/utils';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

import { RaisedHandInfo, RaisedHandsMap, ReactionInfo } from './ReactionsContext';
import { getLastRaiseHandEvent, raiseHandKey } from './utils';
import useMembershipsContext from '../MembershipsContext/useMembershipsContext';

interface ReactionListenersProps {
  raisedHands: RaisedHandsMap;
  addRaisedHand: (userId: string, info: RaisedHandInfo) => void;
  removeRaisedHand: (userId: string) => void;
  addReaction: (userId: string, info: ReactionInfo) => void;
}

const useReactionListeners = ({
  raisedHands,
  addRaisedHand,
  removeRaisedHand,
  addReaction,
}: ReactionListenersProps) => {
  const { matrixClient } = useMatrixContext();
  const { mxRtcSession } = useDefinedMatrixValues();
  const { memberships } = useMembershipsContext();
  const latestMembershipsRef = useLatest(memberships);
  const latestRaisedHandsRef = useLatest(raisedHands);
  const { audioOutputId } = useLiveKitDevicesStateContext();
  const { playSound } = useSoundProvider();

  const room = mxRtcSession.room;

  useEffect(() => {
    const handleReactionEvent = async (event: MatrixEvent) => {
      if (event.getRoomId() !== room.roomId || event.isSending()) return;

      const [sender, eventId] = [event.getSender(), event.getId()];
      if (!sender || !eventId) return;

      await matrixClient.decryptEventIfNeeded(event);
      const eventType = event.getType();

      if ([EventType.Reaction, CustomMeedioMatrixEvents.REACTION].includes(eventType)) {
        const content = event.getContent();
        const membershipEventId = content['m.relates_to']?.event_id;
        const isEventPresent = !latestMembershipsRef.current.some(
          (e) => e.eventId === membershipEventId && e.sender === sender
        );

        if (isEventPresent) return logger.warn(`Reaction target was not a membership event for ${sender}, ignoring`);

        if (membershipEventId && content?.['m.relates_to']?.key === raiseHandKey) {
          addRaisedHand(sender, { raiseHandEventId: eventId, membershipEventId });
          playSound(notificationBeep, { volume: 0.5 });
        }

        if (membershipEventId && emojis.some(({ id }) => id === content?.key)) {
          addReaction(sender, {
            reactionEventId: eventId,
            membershipEventId,
            reactionKey: content?.key || '',
          });
        }
      }

      if (eventType === EventType.RoomRedaction) {
        const targetEventId = event.event.redacts;
        const targetUserEntry = Array.from(latestRaisedHandsRef.current.entries()).find(
          ([_, raiseHandEntry]) => raiseHandEntry.raiseHandEventId === targetEventId
        );
        const targetUserId = targetUserEntry?.[0];

        if (targetUserId) removeRaisedHand(targetUserId);
      }
    };

    room.on(MatrixRoomEvent.Timeline, handleReactionEvent);
    room.on(MatrixRoomEvent.Redaction, handleReactionEvent);
    room.on(MatrixRoomEvent.LocalEchoUpdated, handleReactionEvent);

    return () => {
      room.off(MatrixRoomEvent.Timeline, handleReactionEvent);
      room.off(MatrixRoomEvent.Redaction, handleReactionEvent);
      room.off(MatrixRoomEvent.LocalEchoUpdated, handleReactionEvent);
    };
  }, [
    addRaisedHand,
    addReaction,
    audioOutputId,
    latestMembershipsRef,
    latestRaisedHandsRef,
    matrixClient,
    playSound,
    removeRaisedHand,
    room,
  ]);

  useEffect(() => {
    //NOTE: handle reactions on initial join and when memberships change
    memberships.forEach((membership) => {
      if (!membership.sender || !membership.eventId) return;

      const reaction = getLastRaiseHandEvent({ room, eventId: membership.eventId, expectedSender: membership.sender });
      if (!reaction) return;

      const eventId = reaction.getId();
      if (!eventId) return;

      addRaisedHand(membership.sender, { membershipEventId: membership.eventId, raiseHandEventId: eventId });
    });
  }, [room, memberships, addRaisedHand, removeRaisedHand, latestRaisedHandsRef]);

  useEffect(() => {
    //NOTE: remove abandoned raise hand entries
    latestRaisedHandsRef.current.forEach((value, key) => {
      const isUserInCall = memberships.some(({ eventId }) => value.membershipEventId === eventId);
      if (!isUserInCall) removeRaisedHand(key);
    });
  }, [latestRaisedHandsRef, memberships, removeRaisedHand]);
};

export default useReactionListeners;
